import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery as useConvexQuery, useMutation } from "convex/react";
import { api } from "../../api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Check, ArrowLeft, Search } from "lucide-react";
import { AppIcon } from "./AppIcon";
import { Skeleton } from "@/components/ui/skeleton";

export const VerifiedAppsTable = () => {
  const navigate = useNavigate();
  const [verifiedAppIds, setVerifiedAppIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch all apps using pagination
  const appsData = useConvexQuery(api.apps.list.all, {
    paginationOpts: {
      cursor: null,
      numItems: 100,
    },
  });

  // Fetch categories for the dropdown
  const categories = useConvexQuery(api.appCategories.list.categories);

  const updateAppCategory = useMutation(api.apps.update.category);
  const updateAppVerified = useMutation(api.apps.update.verified);

  const handleVerify = async (appId: string, appName: string) => {
    try {
      // Optimistically mark as verified
      setVerifiedAppIds(prev => new Set([...prev, appId]));
      
      await updateAppVerified({
        app: appId as any,
        verified: true,
      });
      toast.success(`${appName} has been verified`);
    } catch (error) {
      // Rollback on error
      setVerifiedAppIds(prev => {
        const next = new Set(prev);
        next.delete(appId);
        return next;
      });
      toast.error("Failed to verify app");
      console.error(error);
    }
  };

  const handleCategoryChange = async (appId: string, category: string, appName: string) => {
    try {
      await updateAppCategory({
        app: appId as any,
        category,
      });
      toast.success(`${appName} category updated to ${category}`);
    } catch (error) {
      toast.error("Failed to update app category");
      console.error(error);
    }
  };

  if (!appsData || !categories) {
    return (
      <div className="px-6">
        <Card className="p-6">
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </Card>
      </div>
    );
  }

  // Filter to show only apps with verified unset (null/undefined) or explicitly false
  // Also exclude apps that were just verified (optimistic update)
  const unverifiedApps = appsData.page.filter((app: any) => {
    // Skip apps that were just verified (optimistic UI)
    if (verifiedAppIds.has(app.id)) return false;
    
    // Include apps where verified is unset (undefined/null) or explicitly false
    const isUnverified = app.verified === undefined || app.verified === null || app.verified === false;
    
    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesName = app.name?.toLowerCase().includes(search);
      const matchesBundleId = app.bundleId?.toLowerCase().includes(search);
      return isUnverified && (matchesName || matchesBundleId);
    }
    
    return isUnverified;
  });

  return (
    <div className="px-6">
      {/* Header with back button and search */}
      <div className="flex items-center gap-4 mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/section/apps")}
          className="flex items-center gap-2 -ml-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search apps..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-8"
          />
        </div>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b sticky top-0 bg-background z-10">
                <TableHead className="w-[50px] font-semibold bg-background">Icon</TableHead>
                <TableHead className="font-semibold bg-background">App Name</TableHead>
                <TableHead className="font-semibold bg-background">Category</TableHead>
                <TableHead className="font-semibold w-[80px] bg-background">Type</TableHead>
                <TableHead className="text-right font-semibold w-[100px] bg-background">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {unverifiedApps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <div className="text-muted-foreground">
                      <Check className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="font-medium text-sm">No unverified apps found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                unverifiedApps.map((app: any) => (
                  <TableRow key={app.id} className="hover:bg-muted/50">
                    <TableCell className="py-2">
                      <AppIcon app_icon_url={app.iconUrl} app_name={app.name} size="sm" />
                    </TableCell>
                    <TableCell className="font-medium text-sm py-2">{app.name}</TableCell>
                    <TableCell className="py-2">
                      <Select
                        value={app.category || "Uncategorized"}
                        onValueChange={(value) => handleCategoryChange(app.id, value, app.name)}
                      >
                        <SelectTrigger className="w-[160px] h-8 text-xs">
                          <SelectValue placeholder="Uncategorized" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                          {categories.map((cat) => (
                            <SelectItem key={cat.name} value={cat.name}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge 
                        variant={app.system ? "outline" : "secondary"}
                        className="font-normal text-xs"
                      >
                        {app.system ? "System" : "Store"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right py-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleVerify(app.id, app.name)}
                        className="bg-success hover:bg-success/80 text-success-foreground border-success h-7 gap-1 text-xs px-2"
                      >
                        <Check className="w-3 h-3" />
                        Verify
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};
